import type { TaskComment } from '@obras/shared';
import { formatDate, formatDateTime } from '@obras/shared';
import { Send } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';

import { EASE_OUT_QUINT } from '../../components/motion.js';
import { Button } from '../../components/ui/Button.js';
import { Avatar, Skeleton } from '../../components/ui/Feedback.js';
import { CONTROL_BASE, CONTROL_TONE } from '../../components/ui/Field.js';
import { ApiError } from '../../lib/api.js';
import { cn } from '../../lib/cn.js';
import {
  useComentar,
  useComentarios,
  useExcluirComentario,
  useReescreverComentario,
} from './api.js';

const LIMITE = 4000;

/**
 * Quando foi, em linguagem corrente e no fuso do órgão: "agora", "há 5 min",
 * "hoje às 14:32", "ontem às 09:10" ou "26/10/2026 às 14:32".
 */
function quandoFoi(iso: string, agora = new Date()): string {
  const minutos = Math.floor((agora.getTime() - new Date(iso).getTime()) / 60_000);
  if (minutos < 1) return 'agora';
  if (minutos < 60) return `há ${minutos} min`;

  const [data, hora] = formatDateTime(iso).split(' ');
  if (data === formatDate(agora)) return `hoje às ${hora}`;
  if (data === formatDate(new Date(agora.getTime() - 86_400_000))) return `ontem às ${hora}`;
  return `${data} às ${hora}`;
}

const mensagemDe = (erro: unknown, padrao: string): string =>
  erro instanceof ApiError ? erro.message : padrao;

const CAMPO = cn(CONTROL_BASE, CONTROL_TONE.rest, 'block w-full resize-y py-2 text-sm');

/**
 * Os comentários da tarefa (design.md §8, Tarefas): a conversa de quem
 * participa dela, em ordem de chegada, com o campo de escrever no pé.
 *
 * Cada um traz quem escreveu e quando; o próprio comentário se edita e se
 * exclui ali mesmo — a exclusão pede confirmação na própria linha, sem abrir
 * outra caixa sobre o modal. Ctrl+Enter envia.
 *
 * Mora numa metade do modal, como uma conversa: o título em cima, o campo de
 * escrever fixo embaixo e, no meio, a lista que rola — aberta já no fim, no
 * comentário mais recente, e que desce sozinha quando chega um novo.
 */
export function Comentarios({ taskId }: { taskId: string }) {
  const tituloId = useId();
  const comentarios = useComentarios(taskId);
  const comentar = useComentar(taskId);
  const [texto, setTexto] = useState('');
  const [erro, setErro] = useState<string | null>(null);

  const itens = comentarios.data?.items ?? [];

  // Abre no fim da conversa, e desce até o comentário que chegar.
  const listaRef = useRef<HTMLDivElement>(null);
  const quantos = useRef(0);
  useEffect(() => {
    const lista = listaRef.current;
    if (lista && itens.length > quantos.current) lista.scrollTop = lista.scrollHeight;
    quantos.current = itens.length;
  }, [itens.length]);

  async function enviar(): Promise<void> {
    const conteudo = texto.trim();
    if (!conteudo || comentar.isPending) return;
    setErro(null);
    try {
      await comentar.mutateAsync(conteudo);
      setTexto('');
    } catch (falha) {
      setErro(mensagemDe(falha, 'Não foi possível comentar. Tente de novo.'));
    }
  }

  function aoTeclar(evento: KeyboardEvent<HTMLTextAreaElement>): void {
    if (evento.key === 'Enter' && (evento.ctrlKey || evento.metaKey)) {
      evento.preventDefault();
      void enviar();
    }
  }

  return (
    <section aria-labelledby={tituloId} className="flex flex-col sm:min-h-0 sm:flex-1">
      <div className="flex shrink-0 items-baseline justify-between gap-3">
        <h3 id={tituloId} className="text-[13px] font-medium">
          Comentários
        </h3>
        {comentarios.data ? (
          <span className="tabular text-[12px] text-ink-3">{itens.length}</span>
        ) : null}
      </div>

      <div ref={listaRef} className="sm:min-h-0 sm:flex-1 sm:overflow-y-auto sm:pr-1">
        {comentarios.isPending ? (
          <div aria-hidden className="mt-3 flex gap-3">
            <Skeleton className="size-7 shrink-0 rounded-full" />
            <div className="flex-1">
              <Skeleton className="h-3 w-40 rounded-none" />
              <Skeleton className="mt-2 h-3.5 w-4/5 rounded-none" />
            </div>
          </div>
        ) : comentarios.isError ? (
          <p className="mt-2 text-[13px] text-danger">
            Não foi possível carregar os comentários.{' '}
            <button
              type="button"
              onClick={() => void comentarios.refetch()}
              className="font-medium underline underline-offset-2"
            >
              Tentar de novo
            </button>
          </p>
        ) : itens.length === 0 ? (
          <p className="mt-2 text-[13px] text-ink-3">
            Nenhum comentário ainda. Escreva abaixo para falar com a equipe da tarefa.
          </p>
        ) : (
          <ol className="mt-1 flex flex-col">
            <AnimatePresence initial={false}>
              {itens.map((comentario) => (
                <motion.li
                  key={comentario.id}
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.22, ease: EASE_OUT_QUINT }}
                  className="overflow-hidden"
                >
                  <Comentario taskId={taskId} comentario={comentario} />
                </motion.li>
              ))}
            </AnimatePresence>
          </ol>
        )}
      </div>

      <div className="mt-3 shrink-0 border-t border-line pt-3">
        <textarea
          value={texto}
          rows={2}
          maxLength={LIMITE}
          onChange={(evento) => setTexto(evento.target.value)}
          onKeyDown={aoTeclar}
          placeholder="Escreva um comentário"
          aria-label="Novo comentário"
          className={cn(CAMPO, 'min-h-16')}
        />
        {erro ? <p className="mt-1 text-[12px] text-danger">{erro}</p> : null}
        <div className="mt-2 flex items-center justify-between gap-3">
          <span className="text-[12px] text-ink-3">Ctrl+Enter envia</span>
          <Button
            size="sm"
            variant="primary"
            icon={<Send size={14} strokeWidth={1.75} />}
            loading={comentar.isPending}
            disabled={texto.trim() === ''}
            onClick={() => void enviar()}
          >
            Comentar
          </Button>
        </div>
      </div>
    </section>
  );
}

function Comentario({ taskId, comentario }: { taskId: string; comentario: TaskComment }) {
  const reescrever = useReescreverComentario(taskId);
  const excluir = useExcluirComentario(taskId);
  const [editando, setEditando] = useState(false);
  const [rascunho, setRascunho] = useState(comentario.content);
  const [confirmando, setConfirmando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function salvar(): Promise<void> {
    const conteudo = rascunho.trim();
    if (!conteudo) return;
    setErro(null);
    try {
      await reescrever.mutateAsync({ id: comentario.id, content: conteudo });
      setEditando(false);
    } catch (falha) {
      setErro(mensagemDe(falha, 'Não foi possível salvar. Tente de novo.'));
    }
  }

  function aoTeclar(evento: KeyboardEvent<HTMLTextAreaElement>): void {
    if (evento.key === 'Enter' && (evento.ctrlKey || evento.metaKey)) {
      evento.preventDefault();
      void salvar();
    } else if (evento.key === 'Escape') {
      // O Esc é da edição: desiste dela sem fechar o modal.
      evento.preventDefault();
      evento.stopPropagation();
      setEditando(false);
    }
  }

  return (
    <div className="flex gap-3 border-b border-line py-3">
      <Avatar name={comentario.author.name} url={comentario.author.avatarUrl} size={28} />

      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-baseline gap-x-2 text-[13px]">
          <span className="font-medium text-ink">{comentario.author.name}</span>
          <time
            dateTime={comentario.createdAt}
            title={formatDateTime(comentario.createdAt)}
            className="text-[12px] text-ink-3"
          >
            {quandoFoi(comentario.createdAt)}
          </time>
          {comentario.editedAt ? (
            <span className="text-[12px] text-ink-3" title={formatDateTime(comentario.editedAt)}>
              (editado)
            </span>
          ) : null}
        </p>

        {editando ? (
          <div className="mt-1.5">
            <textarea
              // A edição nasce do clique em "Editar": o foco vai para o texto.
              autoFocus
              value={rascunho}
              rows={3}
              maxLength={LIMITE}
              onChange={(evento) => setRascunho(evento.target.value)}
              onKeyDown={aoTeclar}
              aria-label="Texto do comentário"
              className={CAMPO}
            />
            {erro ? <p className="mt-1 text-[12px] text-danger">{erro}</p> : null}
            <div className="mt-2 flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={() => setEditando(false)}>
                Cancelar
              </Button>
              <Button
                size="sm"
                variant="primary"
                loading={reescrever.isPending}
                disabled={rascunho.trim() === ''}
                onClick={() => void salvar()}
              >
                Salvar
              </Button>
            </div>
          </div>
        ) : (
          <p className="mt-1 text-sm break-words whitespace-pre-wrap text-ink-2">
            {comentario.content}
          </p>
        )}

        {comentario.canEdit && !editando ? (
          confirmando ? (
            <p className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px]">
              <span className="text-ink-2">Excluir este comentário?</span>
              <button
                type="button"
                disabled={excluir.isPending}
                onClick={() =>
                  excluir.mutate(comentario.id, {
                    onError: (falha) => {
                      setConfirmando(false);
                      setErro(mensagemDe(falha, 'Não foi possível excluir. Tente de novo.'));
                    },
                  })
                }
                className="font-medium text-danger transition-opacity disabled:opacity-50"
              >
                Excluir
              </button>
              <button
                type="button"
                onClick={() => setConfirmando(false)}
                className="text-ink-3 transition-colors hover:text-ink"
              >
                Cancelar
              </button>
            </p>
          ) : (
            <p className="mt-1.5 flex gap-3 text-[12px] text-ink-3">
              <button
                type="button"
                onClick={() => {
                  setRascunho(comentario.content);
                  setErro(null);
                  setEditando(true);
                }}
                className="transition-colors hover:text-ink"
              >
                Editar
              </button>
              <button
                type="button"
                onClick={() => setConfirmando(true)}
                className="transition-colors hover:text-danger"
              >
                Excluir
              </button>
            </p>
          )
        ) : null}

        {erro && !editando ? <p className="mt-1 text-[12px] text-danger">{erro}</p> : null}
      </div>
    </div>
  );
}
